import React, { useId } from 'react'
import PropTypes from 'prop-types'
import './WidgetContainer.scss'

/**
 * WidgetContainer - Wrapper for dashboard widgets with header
 *
 * @param {React.ReactNode} children - Widget content
 * @param {string} title - Widget title
 * @param {string} subtitle - Optional subtitle
 * @param {React.ReactNode} action - Action element (button, link)
 * @param {string} actionLabel - Action button text (if using default button)
 * @param {Function} onAction - Action click handler
 * @param {boolean} loading - Show loading overlay
 * @param {boolean} fullWidth - Full width container
 * @param {boolean} noPadding - Remove content padding
 * @param {string} className - Additional CSS classes
 * @param {string} ariaLabel - Aria label for the widget region
 */
const WidgetContainer = ({
	children,
	title,
	subtitle,
	action,
	actionLabel,
	onAction,
	loading = false,
	fullWidth = false,
	noPadding = false,
	className = '',
	ariaLabel
}) => {
	const titleId = useId()
	const containerClass = [
		'widget-container',
		fullWidth ? 'widget-container--full-width' : '',
		noPadding ? 'widget-container--no-padding' : '',
		loading ? 'widget-container--loading' : '',
		className
	].filter(Boolean).join(' ')

	const renderAction = () => {
		if (action) return action

		if (actionLabel && onAction) {
			return (
				<button className="widget-container__action" onClick={onAction}>
					{actionLabel}
				</button>
			)
		}

		return null
	}

	return (
		<section
			className={containerClass}
			role="region"
			aria-labelledby={title ? titleId : undefined}
			aria-label={!title && ariaLabel ? ariaLabel : undefined}
			aria-busy={loading}
		>
			{(title || action || actionLabel) && (
				<div className="widget-container__header">
					<div className="widget-container__title-group">
						{title && (
							<h3
								id={titleId}
								className="widget-container__title"
							>
								{title}
							</h3>
						)}
						{subtitle && <p className="widget-container__subtitle">{subtitle}</p>}
					</div>
					{renderAction()}
				</div>
			)}

			<div className="widget-container__content">
				{children}
			</div>

			{loading && (
				<div
					className="widget-container__loading-overlay"
					role="progressbar"
					aria-label="Loading widget content"
					aria-valuetext="Loading..."
				>
					<div className="widget-container__spinner" aria-hidden="true"></div>
				</div>
			)}
		</section>
	)
}

WidgetContainer.propTypes = {
	children: PropTypes.node,
	title: PropTypes.string,
	subtitle: PropTypes.string,
	action: PropTypes.node,
	actionLabel: PropTypes.string,
	onAction: PropTypes.func,
	loading: PropTypes.bool,
	fullWidth: PropTypes.bool,
	noPadding: PropTypes.bool,
	className: PropTypes.string,
	ariaLabel: PropTypes.string
}

export default WidgetContainer
